import {
  CalendarOutlined,
  CloseCircleFilled,
  DownOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Popover, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useState } from 'react';

interface DateRangeFilterProps {
  label: string;
  fromValue: string;
  toValue: string;
  onChange: (from: string, to: string) => void;
}

export default function DateRangeFilter({
  label,
  fromValue,
  toValue,
  onChange,
}: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [pendingFrom, setPendingFrom] = useState<Dayjs | null>(null);
  const [pendingTo, setPendingTo] = useState<Dayjs | null>(null);

  const isActive = fromValue || toValue;

  const handleOpenChange = (visible: boolean) => {
    if (visible) {
      setPendingFrom(fromValue ? dayjs(fromValue) : null);
      setPendingTo(toValue ? dayjs(toValue) : null);
    }
    setOpen(visible);
  };

  const handleApply = () => {
    onChange(
      pendingFrom ? pendingFrom.startOf('day').toISOString() : '',
      pendingTo ? pendingTo.endOf('day').toISOString() : '',
    );
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setPendingFrom(null);
    setPendingTo(null);
    onChange('', '');
  };

  const content = (
    <div style={{ width: 260, padding: 4 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 12 }}>
        {label}
      </Typography.Text>

      <Typography.Text
        style={{ display: 'block', marginBottom: 4, color: '#757A89' }}
      >
        From
      </Typography.Text>
      <DatePicker
        value={pendingFrom}
        onChange={(date) => setPendingFrom(date)}
        style={{ width: '100%', marginBottom: 12 }}
        placeholder="mm/dd/yyyy"
        format="MM/DD/YYYY"
      />

      <Typography.Text
        style={{ display: 'block', marginBottom: 4, color: '#757A89' }}
      >
        To
      </Typography.Text>
      <DatePicker
        value={pendingTo}
        onChange={(date) => setPendingTo(date)}
        style={{ width: '100%', marginBottom: 16 }}
        placeholder="mm/dd/yyyy"
        format="MM/DD/YYYY"
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          onClick={handleCancel}
          style={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleApply}
          style={{
            flex: 1,
            backgroundColor: '#1C8271',
            borderColor: '#1C8271',
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
    >
      <Button
        icon={<CalendarOutlined />}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          borderColor: isActive ? '#1C8271' : undefined,
          color: isActive ? '#1C8271' : undefined,
        }}
      >
        {label}{' '}
        {isActive ? (
          <CloseCircleFilled
            onClick={handleClear}
            style={{ color: '#1C8271', fontSize: 16 }}
          />
        ) : (
          <DownOutlined style={{ fontSize: 10 }} />
        )}
      </Button>
    </Popover>
  );
}
